import React, { useState } from 'react';

import { Title } from 'src/application/ContentZone/components/common/Widget/Title';
import { TitleCaption } from 'src/application/ContentZone/components/common/Widget/TitleCaption';
import { StudentWidgetWrapper } from 'src/application/ContentZone/components/Widgets/StudentWidgetWrapper';
import { useContentRequirementsContext } from 'src/application/features/RestrictNavigation/ContentRequirementsProvider';
import { useIntlReexport } from 'src/application/hooks/useIntlReexport';
import { quizMessages } from 'src/translates/quizMessages';
import { widgetMessages } from 'src/translates/widgetMessages';

import { useGetSingleImageAssetData } from '../../hooks/useGetMultipleAssetUrls';
import { QuestionFooter } from '../_shared/Question/molecules/QuestionFooter';
import { SubmitStatus, SubmittedData } from '../_shared/Question/QuestionComponent';
import { QuestionWidgetTitleImage } from '../_shared/Question/QuetionWidgetTitleImage';
import { ExistingStudentWidgetProps } from '../Widget';
import { FillInQuestionWidget as FillInQuestionWidgetModel, WidgetType } from '@ael/core-api';
import { StudentButton, UnderlineTextInputWithDefaultValue } from '@ael/shared-components-library';

import './FillInQuestionWidget.scss';

export const FillInQuestionWidget: React.FC<ExistingStudentWidgetProps> = ({ widget }) => {
  const { formatMessage } = useIntlReexport();
  const [answer, setAnswer] = useState<string>('');

  const questionWidget = widget as FillInQuestionWidgetModel;
  const value = questionWidget.value;

  const { questionText, validAnswers, titleAssetId } = value.data;
  const { areAnswersCaseSensitive, repeatUntilCorrect, revealAnswers } = value.properties;

  const customFeedback = {
    feedbackCorrect: value.data.feedbackCorrect,
    feedbackIncorrect: value.data.feedbackIncorrect,
  };

  const titleAssetData = useGetSingleImageAssetData(widget._id, WidgetType.fillInQuestion, titleAssetId);

  const tryAgain = () => {
    setAreOptionsSubmitted(false);
    setAnswer('');
  };

  const [isAnswerSubmitted, setAreOptionsSubmitted] = useState<boolean>(answer !== '');
  const { submitWidget } = useContentRequirementsContext();

  const submittedData = React.useMemo(
    () => calculateSubmitData(isAnswerSubmitted, answer, validAnswers, areAnswersCaseSensitive),
    [answer, areAnswersCaseSensitive, isAnswerSubmitted, validAnswers]
  );

  const submitAnswer = () => {
    if (answer !== '') {
      setAreOptionsSubmitted(true);
      const submitStatus = calculateSubmitData(true, answer, validAnswers, areAnswersCaseSensitive);
      const fulfillWithIncorrect = !repeatUntilCorrect;
      submitWidget(questionWidget, submitStatus.status === SubmitStatus.SubmittedAndSucceded, fulfillWithIncorrect);
    }
  };

  return (
    <StudentWidgetWrapper>
      <div className="fillin-widget uno-bg-surface uno-border-rd-2 uno-border-stroke uno-p-4">
        <TitleCaption>{formatMessage(widgetMessages.fillInWidgetFillInAnswer)}</TitleCaption>
        <Title>
          <div dangerouslySetInnerHTML={{ __html: questionText }}></div>
        </Title>

        {titleAssetData && (
          <div className="uno-pb-4">
            <QuestionWidgetTitleImage assetData={titleAssetData} />
          </div>
        )}

        <UnderlineTextInputWithDefaultValue
          defaultValue="Type in your answer"
          value={answer}
          onChange={setAnswer}
          disabled={submittedData.status !== SubmitStatus.NotSubmitted}
        />

        <div className="uno-mt-4">
          <QuestionFooter
            submittedData={submittedData}
            button={
              submittedData.status === SubmitStatus.NotSubmitted ? (
                <StudentButton onClick={submitAnswer} disabled={answer === ''}>
                  {formatMessage(quizMessages.submit)}
                </StudentButton>
              ) : (
                <StudentButton onClick={tryAgain}>{formatMessage(quizMessages.tryAgain)}</StudentButton>
              )
            }
            customFeedback={customFeedback}
            repeatUntilCorrect={repeatUntilCorrect}
          />
        </div>

        {submittedData.status === SubmitStatus.SubmittedAndFailed && revealAnswers && (
          <div className="uno-mt-4 uno-text-grey-500 uno-italic">
            {formatMessage(widgetMessages.fillInWidgetAcceptedAnswers)}{' '}
            {validAnswers.filter((answer) => answer).join(', ')}
          </div>
        )}
      </div>
    </StudentWidgetWrapper>
  );
};

const isAnswerCorrect = (answer: string, validAnswers: string[], areAnswersCaseSensitive: boolean): boolean => {
  const trimmedAnswer = answer.trim();

  if (!trimmedAnswer) {
    return false;
  } else if (validAnswers.filter((answer) => answer).length === 0) {
    return true;
  }

  if (areAnswersCaseSensitive) {
    return validAnswers.some((validAnswer) => validAnswer.trim() === trimmedAnswer);
  }

  return validAnswers.some((validAnswer) => validAnswer.trim().toLowerCase() === trimmedAnswer.toLowerCase());
};

const calculateSubmitData = (
  wasSubmitted: boolean,
  answer: string,
  validAnswers: string[],
  areAnswersCaseSensitive: boolean
): SubmittedData => {
  if (!wasSubmitted) {
    return { status: SubmitStatus.NotSubmitted };
  }

  if (isAnswerCorrect(answer, validAnswers, areAnswersCaseSensitive)) {
    return {
      status: SubmitStatus.SubmittedAndSucceded,
    };
  }

  return {
    status: SubmitStatus.SubmittedAndFailed,
    correct: [],
    incorrect: [],
  };
};
